import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Dialog,
  Field,
  Icon,
  Input,
  Metric,
  Switcher,
  Table,
  type Column,
} from '@/components/primitives';
import { PageHeader } from '@/components/layout/PageHeader';
import { CalibrationBanner, useDataSnapshotSummary } from '@/components/common/CalibrationBanner';
import { useViewport } from '@/lib/viewport';
import { useAuth } from '@/lib/auth';
import { useScope } from '@/lib/scope';
import { useCurrentConfig } from '@/hooks/useConfig';
import { configService } from '@/services/configService';
import { auditService } from '@/services/auditService';
import type { ConfigValue, PkType, ProductGroup } from '@/domain';

const PRODUCT_GROUPS: ProductGroup[] = [
  'COSMETIC',
  'FMCG',
  'ELECTRONICS',
  'APPAREL',
  'OTHER',
];
const PK_TYPES: PkType[] = ['STANDARD', 'BULKY', 'FRAGILE', 'COMBO'];

const PG_LABEL: Record<ProductGroup, string> = {
  COSMETIC: 'Mỹ phẩm',
  FMCG: 'FMCG',
  ELECTRONICS: 'Điện tử',
  APPAREL: 'Thời trang',
  OTHER: 'Khác',
};
const PK_LABEL: Record<PkType, string> = {
  STANDARD: 'Tiêu chuẩn',
  BULKY: 'Hàng cồng kềnh',
  FRAGILE: 'Dễ vỡ',
  COMBO: 'Combo',
};

const DEFAULT_CUSTOMER_ID = 'DEFAULT';

interface CellEdit {
  warehouse_id: string;
  warehouse_code: string;
  product_group: ProductGroup;
  pk_type: PkType;
  value: number;
}

export function NormsConfigPage() {
  const { isMobile, vw } = useViewport();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { warehouses, currentWarehouse, setWarehouseId } = useScope();
  const [whFilter, setWhFilter] = useState<string>(currentWarehouse?.id ?? '');
  const [editing, setEditing] = useState<CellEdit | null>(null);
  const norms = useCurrentConfig('UPH_NORM');

  const targetWh = warehouses.find((w) => w.id === (whFilter || currentWarehouse?.id)) ?? null;

  const grid = useMemo(() => {
    if (!targetWh) return new Map<string, ConfigValue>();
    const m = new Map<string, ConfigValue>();
    for (const n of norms) {
      // key format: uph:{warehouse_id}:{customer_id}:{product_group}:{pk_type}
      const parts = n.key.split(':');
      const whId = parts[1];
      const custId = parts[2];
      const pg = parts[3];
      const pk = parts[4];
      if (whId !== targetWh.id || custId !== DEFAULT_CUSTOMER_ID) continue;
      m.set(`${pg}:${pk}`, n);
    }
    return m;
  }, [norms, targetWh]);

  const values = useMemo(() => {
    const arr: number[] = [];
    grid.forEach((v) => arr.push(v.value));
    return arr;
  }, [grid]);

  const minUph = values.length ? Math.min(...values) : 0;
  const maxUph = values.length ? Math.max(...values) : 0;
  const avgUph = values.length
    ? Math.round(values.reduce((s, v) => s + v, 0) / values.length)
    : 0;
  const avgMinPerOrder = avgUph > 0 ? (60 / avgUph).toFixed(2) : '—';

  async function saveCell() {
    if (!editing || !user) return;
    await configService.setValue({
      key: {
        kind: 'UPH_NORM',
        warehouse_id: editing.warehouse_id,
        customer_id: DEFAULT_CUSTOMER_ID,
        product_group: editing.product_group,
        pk_type: editing.pk_type,
      },
      value: editing.value,
      note: '',
      actor: user.email,
    });
    await auditService.log({
      actor: user.email,
      action: 'CONFIG_CHANGE',
      entity_type: 'config_value',
      entity_id: `uph:${editing.warehouse_id}:${DEFAULT_CUSTOMER_ID}:${editing.product_group}:${editing.pk_type}`,
      summary: `Cập nhật UPH mặc định ${editing.warehouse_code} · ${editing.product_group} · ${editing.pk_type} thành ${editing.value}`,
      payload: { ...editing, customer_id: DEFAULT_CUSTOMER_ID },
    });
    setEditing(null);
  }

  type Row = { product_group: ProductGroup } & Record<PkType, ConfigValue | undefined>;

  const rows: Row[] = useMemo(
    () =>
      PRODUCT_GROUPS.map((pg) => {
        const r = { product_group: pg } as Row;
        for (const pk of PK_TYPES) {
          r[pk] = grid.get(`${pg}:${pk}`);
        }
        return r;
      }),
    [grid],
  );

  const cols: Column<Row>[] = [
    {
      header: 'Nhóm sản phẩm',
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600, color: 'var(--bx-text-dark)' }}>
            {PG_LABEL[r.product_group]}
          </div>
          <div
            style={{
              fontSize: 11,
              color: 'var(--bx-text-muted)',
              fontFamily: 'var(--bx-font-mono)',
            }}
          >
            {r.product_group}
          </div>
        </div>
      ),
    },
    ...PK_TYPES.map(
      (pk) =>
        ({
          header: (
            <div>
              <div>{PK_LABEL[pk]}</div>
              <div style={{ fontSize: 9.5, color: 'var(--bx-text-muted)', marginTop: 2 }}>
                {pk}
              </div>
            </div>
          ),
          align: 'right' as const,
          render: (r: Row) => {
            const cell = r[pk];
            if (!cell || !targetWh)
              return <span style={{ color: 'var(--bx-text-muted)' }}>—</span>;
            return (
              <button
                type="button"
                onClick={() =>
                  setEditing({
                    warehouse_id: targetWh.id,
                    warehouse_code: targetWh.code,
                    product_group: r.product_group,
                    pk_type: pk,
                    value: cell.value,
                  })
                }
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'var(--bx-font-mono)',
                  fontVariantNumeric: 'tabular-nums',
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--bx-text-dark)',
                  padding: '6px 8px',
                  borderRadius: 6,
                  width: '100%',
                  textAlign: 'right',
                }}
                title={`Sửa UPH mặc định cho ${PG_LABEL[r.product_group]} × ${PK_LABEL[pk]}`}
              >
                {cell.value}
              </button>
            );
          },
        }) as Column<Row>,
    ),
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <PageHeader
        breadcrumb={['Cấu hình', 'Tham số vận hành', 'Định mức', 'Mặc định']}
        title="Định mức UPH mặc định"
        subtitle="Áp dụng cho khách hàng chưa có cấu hình riêng. Engine M4 derive WLU = 60 / UPH."
        actions={
          <>
            <Switcher
              icon="warehouse"
              label="Kho"
              value={targetWh?.id ?? ''}
              options={warehouses.map((w) => ({
                value: w.id,
                label: w.code,
                sublabel: w.name,
              }))}
              onChange={(v) => {
                setWhFilter(v);
                setWarehouseId(v);
              }}
              width={180}
            />
            <Button
              variant="outline"
              icon="grid"
              onClick={() => navigate('/admin/config/dinhmuc')}
            >
              Bảng tổng hợp
            </Button>
            <Button
              variant="outline"
              icon="user"
              onClick={() => navigate('/admin/config/dinhmuc-khachhang')}
            >
              Theo khách hàng
            </Button>
          </>
        }
      />
      <div style={{ flex: 1, padding: isMobile ? 14 : 20 }}>
        <CalibrationBanner subtitle={useDataSnapshotSummary()} />
        <div
          style={{
            display: 'grid',
            gap: 12,
            marginBottom: 16,
            gridTemplateColumns: vw < 720 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          }}
        >
          <Metric label="UPH trung bình" value={avgUph} sublabel="units / hour" />
          <Metric label="UPH cao nhất" value={maxUph} sublabel="nhanh nhất" deltaTone="up" />
          <Metric label="UPH thấp nhất" value={minUph} sublabel="chậm nhất" deltaTone="down" />
          <Metric
            label="Trung bình phút/đơn"
            value={avgMinPerOrder}
            sublabel="60 / UPH_avg"
            hint="WLU base"
          />
        </div>

        {targetWh ? (
          <Card
            title={`Ma trận UPH mặc định · ${targetWh.code}`}
            subtitle="Click vào ô số để sửa. Mọi thay đổi tạo version mới + audit."
            padding={0}
          >
            <Table columns={cols} rows={rows} getRowKey={(r) => r.product_group} />
          </Card>
        ) : (
          <Card>
            <div style={{ textAlign: 'center', padding: 24, color: 'var(--bx-text-muted)' }}>
              Chọn kho ở thanh trên để xem ma trận UPH
            </div>
          </Card>
        )}

        <Card title="Ghi chú vận hành" style={{ marginTop: 16 }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              fontSize: 13,
              color: 'var(--bx-text-body)',
              lineHeight: 1.55,
            }}
          >
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <Icon name="info" size={14} style={{ color: 'var(--bx-primary)', marginTop: 2 }} />
              <span>
                Đây là định mức <strong>mặc định</strong>. Khi khách hàng có cấu hình riêng,
                engine sẽ ưu tiên dùng cấu hình đó.
              </span>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <Icon name="warning" size={14} style={{ color: '#d97706', marginTop: 2 }} />
              <span>
                Sửa UPH ảnh hưởng trực tiếp tới shift plan và hiring forecast. Cần Ops sign-off.
              </span>
            </div>
          </div>
        </Card>
      </div>

      <Dialog
        open={!!editing}
        onClose={() => setEditing(null)}
        title={
          editing
            ? `Sửa UPH mặc định · ${editing.warehouse_code} · ${PG_LABEL[editing.product_group]} · ${PK_LABEL[editing.pk_type]}`
            : ''
        }
        subtitle="Tạo phiên bản mới — bản cũ giữ lại"
        width={460}
        footer={
          <>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Hủy
            </Button>
            <Button variant="primary" icon="check" onClick={saveCell}>
              Tạo phiên bản mới
            </Button>
          </>
        }
      >
        {editing && (
          <Field label="UPH mới" hint="Units per hour — số nguyên dương">
            <Input
              type="number"
              value={editing.value}
              onChange={(e) =>
                setEditing({ ...editing, value: Math.max(0, Number(e.target.value) || 0) })
              }
            />
          </Field>
        )}
      </Dialog>
    </div>
  );
}
